import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TranslationsService } from '../translations.service';
import { ChangesToUndo, SprogLag, SprogLagLanguageMap, UndoAction } from '../translations.model';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { Pipe, PipeTransform } from '@angular/core';
import { CommonService } from 'src/app/services/common.service';
import { environment } from 'src/environments/environment';
import { ModalService } from 'src/app/services/modal.service';
import { ErrorService } from 'src/app/common/errors/error.service';
import { ActivatedRoute } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgClass, NgStyle, DatePipe, UpperCasePipe } from '@angular/common';
import { TranslationsFieldComponent } from '../translations-field/translations-field.component';
import { PaginationListFooterComponent } from '../pagination-list-footer/pagination-list-footer.component';

@Pipe({ name: 'tabIndex' })
export class TabIndexPipe implements PipeTransform {
  transform(row: number, column: number, cultureIdsLength: number): number {
    return row * (cultureIdsLength + 1) + column + 1;
  }
}

@Pipe({ name: 'dateColor' })
export class DateColorPipe implements PipeTransform {
  transform(date: string | undefined, lastMigrationDate: Date): string {
    if (date === undefined) return '#add8e6';

    var input = new Date(date);
    return input <= lastMigrationDate ? 'white' : '#add8e6';
  }
}

@Component({
    selector: 'app-translations-list',
    templateUrl: './translations-list.component.html',
    styleUrls: ['../translations.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, NgbTooltip, NgClass, NgStyle, TranslationsFieldComponent, PaginationListFooterComponent, DatePipe, TabIndexPipe, DateColorPipe, UpperCasePipe]
})
export class TranslationsListComponent implements OnInit, OnDestroy, AfterViewInit {
  // environment variables
  latestImportDate = new Date();
  editAllowed: boolean = environment.environment === 'Development';

  //data values
  sprogLagLanguageMap: SprogLagLanguageMap[] = [];
  sprogLagLanguageMapFiltered: SprogLagLanguageMap[] = [];
  visibleItems: SprogLagLanguageMap[] = [];

  cultureIds: string[] = [];

  //ui values
  searchControlGlobal: FormControl<string>;
  searchControlFieldname: FormControl<string>;
  searchControlsForCultureIds: FormArray<FormControl<string>>;
  dateControl: FormControl<string | null>;
  isBefore: boolean = true;
  searchSelectedFieldname: boolean = true;

  //url filter params (used for hyperlink slicing)
  urlFeltNavn: string | undefined;
  urlVaerdi: string | undefined;

  // ViewChild with template reference #constantInput
  @ViewChild('constantInput', { static: false }) constantInput!: ElementRef;
  @ViewChild('fieldNames') fieldNamesColumn!: ElementRef;

  //loading booleans
  isLoading = true;
  loadingError = false;

  //pagination
  itemHeight = 46; // Height of each item in the list
  itemsPerPage = 20; // Number of items per page
  currentPage = 1; // Current page
  totalPages = -1; // Total number of pages
  //visible items index
  startIndex = 0;
  endIndex = 0;
  unaligned = false; //indicates whether or not the current page is unaligned with the items per page
  //this will be due to editing items and the items per page changing, which will then be fixed on the first page navigation, to improve UX

  //Undo stack for undoing changes, list of list of changes to handle multiple lines being pasted at once
  undoStack: UndoAction[] = [];

  constructor(
    private translationsService: TranslationsService,
    private fb: FormBuilder,
    private common: CommonService,
    private modal: ModalService,
    private errorService: ErrorService,
    private route: ActivatedRoute
  ) {
    this.searchControlsForCultureIds = fb.array<FormControl<string>>([]);
    this.searchControlFieldname = fb.nonNullable.control<string>('');
    this.searchControlGlobal = fb.nonNullable.control<string>('');
    this.dateControl = fb.control<string | null>(null);
  }


  //#region Ng methods

  async ngOnInit(): Promise<void> {
    try {
      const [cultureIds, sprogLagLanguageMap] = await Promise.all([
        this.translationsService.getCultureIds(),
        this.getTranslations(),
      ]);
      this.cultureIds = cultureIds;
      this.sprogLagLanguageMap = sprogLagLanguageMap;

      if (environment.environment != 'Development') {
        this.latestImportDate = new Date(await this.translationsService.getLatestImportDate());
      } else {
        this.latestImportDate.setHours(0, 0, 0, 0); //Sæt dato til starten af dagen.
      }

      this.sprogLagLanguageMapFiltered = this.sprogLagLanguageMap; //copy to the filter list
      this.updateVisibleItems(); //update the visible items that show on the current page

      // Global search
      this.searchControlGlobal.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(value => {
        this.currentPage = 1;
        this.search();
      });

      // Subscribe to search control value changes with debounce time
      this.searchControlFieldname.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(value => {
        this.currentPage = 1;
        this.search();
      });

      this.cultureIds.forEach(cultureId => {
        const control = this.fb.nonNullable.control<string>('');
        control.valueChanges.pipe(
          debounceTime(300)
        ).subscribe(value => {
          this.currentPage = 1;
          this.search();
        });

        this.searchControlsForCultureIds.push(control);
      });

      this.dateControl.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(value => {
        console.log(value);

        this.currentPage = 1;
        this.search();
      });

      // Subscribe to the top bar button event
      this.common.ImportTranslations.subscribe((emittedString: string) => {
        this.modal.showTranslationsImportModal(emittedString).then(async (result: any) => {
          // refresh the page
          if (result == 'refresh') {
            window.location.reload();
          }
        }, (reason: any) => {
        });
      });

      // Add global event listener for keybinds and resizing
      window.addEventListener('keydown', this.handleKeybinds.bind(this));
      window.addEventListener('resize', this.onResize.bind(this));
    } catch {
      this.loadingError = true;
    } finally {
      this.isLoading = false;
      //Den skjulte lige pludselig pagination footer, det her fikser det men det er lidt hacky.
      setTimeout(() => {
        this.calculateItemsPerPage();
      }, 0);
    }

  }

  ngAfterViewInit(): void {
    this.focusFirst();
  }

  ngOnDestroy(): void {
    // Remove global event listeners
    window.removeEventListener('keydown', this.handleKeybinds.bind(this));
    window.removeEventListener('resize', this.onResize.bind(this));
  }

  //#endregion

  //#region CRUD

  addNewTranslation(): void {
    const newMap: SprogLagLanguageMap = {
      feltNavn: '',
      languageMap: this.cultureIds.reduce((acc, cultureId) => {
        acc[cultureId] = this.createBlankSprogLag('', cultureId);
        return acc;
      }, {} as { [key: string]: SprogLag }),
      isEdited: true, // Mark the new item as edited
      waitingForNetwork: false,
    };
    this.sprogLagLanguageMap.unshift(newMap); // Add the new item to the top of the list
    this.visibleItems.unshift(newMap);
    if (this.visibleItems.length > this.itemsPerPage) {
      this.visibleItems.pop(); // Remove the last item from the list if we would exceed the total pages
    }
    this.recalculateCurrentPage();

    this.focusFirst();
  }

  async getTranslations(): Promise<SprogLagLanguageMap[]> {
    this.urlFeltNavn = this.route.snapshot.queryParams['feltnavn'] || undefined;
    this.urlVaerdi = this.route.snapshot.queryParams['vaerdi'] || undefined;
    return await this.translationsService.getTranslations(this.urlFeltNavn, this.urlVaerdi);
  }

  createBlankSprogLag(feltNavn: string, cultureId: string): SprogLag {
    return {
      id: -1,
      kundeId: 0,
      sprogId: 0,
      feltNavn: feltNavn,
      cultureId: cultureId,
      vaerdi: '',
      timestamp: new Date(),
      isEdited: false,
    };
  }

  async saveAll(): Promise<void> {
    const editedItems = this.sprogLagLanguageMap.filter(map => map.isEdited);

    this.save(editedItems);
  }

  async save(maps: SprogLagLanguageMap[]): Promise<void> {
    let edited: SprogLag[] = [];
    for (const map of maps) {
      map.waitingForNetwork = true;
      for (const cultureId of this.cultureIds) {
        const translation = map.languageMap[cultureId];
        const keyChanged = translation.feltNavn !== map.feltNavn;
        //mark as edited if key changed, if id is -1 then it's a new item and is not eligible for this check
        if (keyChanged && translation.id != -1) translation.isEdited = true;
        if (translation.isEdited) {
          translation.feltNavn = map.feltNavn; //sync feltnavn
          translation.frontendGuid = translation.frontendGuid || this.common.newUUID(); //generate new guid if not exists
          edited.push(translation);
        }
      }
    }

    const response = await this.translationsService.saveTranslations(edited);

    for (const map of maps) {
      map.waitingForNetwork = false
    }

    if (!response) return;
    // Move to page 1.
    this.currentPage = 1;

    // Clear edit on all successful translations, the failed ones will be left as is.
    for (const result of response.data) {
      if (!result.error) {
        const translation = edited.find(t => t.frontendGuid === result.frontendGuid);
        if (translation) {
          translation.isEdited = false;
          translation.id = result.id; //if posting a new item, the id will be updated
        }
      }
    }

    // Go through all the items being saved and modify if the save was succesful.
    for (const map of maps) {
      const allFieldsFine = Object.values(map.languageMap).every(value => !value.isEdited);
      if (allFieldsFine) {
        map.isEdited = false;
        map.updatedOn = new Date().toString();
      }
    }
    this.recalculateCurrentPage();
    this.search();

    // Handle the errors.
    const errorResults = response.data.filter(r => r.error);
    for (const result of errorResults) {
      const parentMap = maps.find(map =>
        Object.values(map.languageMap).some(t => t.frontendGuid === result.frontendGuid)
      );
      if (parentMap) {
        parentMap.errorMessage = result.message;
        this.errorService.notify(result.message, { title: parentMap.feltNavn });
      } else {
        this.errorService.notify(result.message);
      }
    }

  }

  async deleteTranslation(element: SprogLagLanguageMap): Promise<void> {
    let result: boolean = await this.common.confirm('slet', 'Er du sikker på at du vil slette denne oversættelse?');
    if (!result) return;
    try {
      const sproglag = Object.values(element.languageMap).map(t => t.id).filter(id => id > 0)
      if (sproglag.length !== 0) {
        element.waitingForNetwork = true;
        const result = await this.translationsService.deleteTranslation(sproglag);
        if (result.error) {
          element.waitingForNetwork = false;
          console.error('Error deleting translation', result.message);
          return;
        }
      }

      const index = this.sprogLagLanguageMap.indexOf(element);
      if (index > -1) {
        this.sprogLagLanguageMap.splice(index, 1);
        this.search();
      }
    } catch (e) {
      element.waitingForNetwork = false;
      console.error('Error deleting translation', e);
    }
  }

  //#endregion

  //#region Searching

  search(): void {
    // Start with the full list
    let filteredList = this.sprogLagLanguageMap;

    // Remove edited items, they will be virtually added to the visible items, this prevents duplicates.
    filteredList = this.removeEditedItemsFromList(filteredList);

    // Apply the constant search filter if it exists
    const constantSearchText = this.searchControlFieldname.value;
    if (constantSearchText) {
      filteredList = filteredList.filter(map =>
        this.matchesSearchTerm(map.feltNavn, constantSearchText)
      );
    }

    // Apply the culture-specific search filters
    if (this.searchControlsForCultureIds.value.some(value => value !== '')) {
      this.cultureIds.forEach((cultureId, index) => {
        const searchText = this.searchControlsForCultureIds.at(index)?.value;
        if (searchText) {
          filteredList = filteredList.filter(map =>
            this.matchesSearchTerm(map.languageMap[cultureId]?.vaerdi || '', searchText)
          );
        }
      });
    }

    // Apply the global search filter if it exists
    const globalSearchText = this.searchControlGlobal.value;
    if (globalSearchText) {
      filteredList = filteredList.filter(map =>
        this.matchesSearchTerm(map.feltNavn, globalSearchText) ||
        this.cultureIds.some(cultureId =>
          this.matchesSearchTerm(map.languageMap[cultureId]?.vaerdi || '', globalSearchText)
        )
      );
    }

    // Sort
    filteredList = this.sort(filteredList);

    // Apply date filter
    filteredList = this.applyDateFilter(filteredList);

    // Update the filtered list
    this.sprogLagLanguageMapFiltered = filteredList;



    // Update the visible items
    this.updateVisibleItems();
  }

/**
* Determines if a text matches a given search term using various search patterns.
* 
* Supported search patterns:
* - `:empty` - matches empty or whitespace-only text
* - `"exact"` - exact match when surrounded by quotes
* - `^term` - text starts with the term
* - `term$` - text ends with the term
* - `wild*card` or `wild?card` - wildcard pattern matching (* = any chars, ? = single char)
* - `\*` or `\?` - escaped wildcards to search for literal * or ? characters
* - `term1||term2||term3` - OR operation (matches any of the groups)
* - `term1&&term2` - AND operation (requires all terms to match)
* - Combined example: `a&&b||c` is evaluated as (a AND b) OR c
* - `partial` - default contains matching (case-insensitive)
* 
* @param text - The text to search in
* @param searchTerm - The search pattern to match against
* @returns true if the text matches the search term, false otherwise
*/
private matchesSearchTerm(text: string, searchTerm: string): boolean {
  if (!searchTerm || searchTerm.trim() === '') return false;

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // Special searches for empty values
  if (normalizedSearchTerm === ':empty') {
    return !text || text.trim() === '';
  }

  // Now check if text is empty for regular searches
  if (!text) return false;

  const normalizedText = text.toLowerCase();

  // Helper to evaluate an AND-group (terms separated by &&)
  const evaluateAndGroup = (group: string): boolean => {
    const andTerms = group.split(/\s*&&\s*/).map(t => t.trim()).filter(t => t.length > 0);
    if (andTerms.length === 0) return false;
    return andTerms.every(term => this.matchesSingleTerm(normalizedText, term));
  };

  // If there is an OR operator (||), split into groups and evaluate each group.
  // Each group may itself contain && which must all match.
  if (normalizedSearchTerm.includes('||')) {
    const orGroups = normalizedSearchTerm.split(/\s*\|\|\s*/).map(g => g.trim()).filter(g => g.length > 0);
    return orGroups.some(group => {
      if (group.includes('&&')) {
        return evaluateAndGroup(group);
      } else {
        return this.matchesSingleTerm(normalizedText, group);
      }
    });
  }

  // If there is no OR but an AND operator (&&), require all terms to match.
  if (normalizedSearchTerm.includes('&&')) {
    return evaluateAndGroup(normalizedSearchTerm);
  }

  // Fallback: single term match
  return this.matchesSingleTerm(normalizedText, normalizedSearchTerm);
}

  /**
 * Helper method to match a single search term against text
 * @param text - normalized text to search in
 * @param searchTerm - normalized search term
 * @returns true if text matches the search term
 */
  private matchesSingleTerm(text: string, searchTerm: string): boolean {
    // Exact match with quotes
    if (searchTerm.startsWith('"') && searchTerm.endsWith('"') && searchTerm.length > 2) {
      const exactTerm = searchTerm.slice(1, -1);
      return text === exactTerm;
    }

    // StartsWith search with ^ prefix
    if (searchTerm.startsWith('^') && searchTerm.length > 1) {
      const term = searchTerm.substring(1);
      return text.startsWith(term);
    }

    // EndsWith search with $ suffix
    if (searchTerm.endsWith('$') && searchTerm.length > 1) {
      const term = searchTerm.substring(0, searchTerm.length - 1);
      return text.endsWith(term);
    }

    // Check if this term has unescaped wildcards
    const hasUnescapedWildcards = this.hasUnescapedWildcards(searchTerm);

    if (hasUnescapedWildcards) {
      // Wildcard pattern matching
      const regexPattern = this.buildWildcardRegex(searchTerm);
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(text);
    } else {
      // Regular contains search (handle escaped characters)
      const unescapedTerm = searchTerm.replace(/\\(.)/g, '$1'); // Remove escape backslashes
      return text.includes(unescapedTerm);
    }
  }

  /**
   * Check if a search term contains unescaped wildcard characters
   * @param searchTerm - the search term to check
   * @returns true if it contains unescaped * or ? characters
   */
  private hasUnescapedWildcards(searchTerm: string): boolean {
    // Look for * or ? that are not preceded by a backslash
    return /(?<!\\)[*?]/.test(searchTerm);
  }

  /**
   * Build a regex pattern from a wildcard search term, handling escaped characters
   * @param searchTerm - the search term with wildcards
   * @returns regex pattern string
   */
  private buildWildcardRegex(searchTerm: string): string {
    return searchTerm
      .replace(/\\(.)/g, '\\$1')  // Escape already escaped characters for regex
      .replace(/\*/g, '.*')       // * matches any characters
      .replace(/\?/g, '.');       // ? matches single character
  }

  removeEditedItemsFromList(list: SprogLagLanguageMap[]): SprogLagLanguageMap[] {
    return list.filter(map => !map.isEdited);
  }

  markEdited(map: SprogLagLanguageMap, event: any): void {
    map.isEdited = true;
    map.errorMessage = undefined;
    this.recalculateCurrentPage();
  }

  // sort alphabetical on fieldname
  sort(list: SprogLagLanguageMap[]): SprogLagLanguageMap[] {
    return list.sort((a, b) => {
      return a.feltNavn.localeCompare(b.feltNavn);
    });
  }

  clearFilters(): void {
    this.searchControlsForCultureIds.controls.forEach(control => control.setValue('', { emitEvent: false }));
    this.searchControlGlobal.setValue('', { emitEvent: false });
    this.searchControlFieldname.setValue('', { emitEvent: false });
    this.dateControl.setValue(null, { emitEvent: false });
    this.isBefore = true;
    this.currentPage = 1;
    this.search();
  }

  searchForLatestImportDate(): void {
    const year = this.latestImportDate.getFullYear();
    const month = String(this.latestImportDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(this.latestImportDate.getDate()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}`;
    this.dateControl.setValue(formattedDate);
    this.isBefore = false;
  }

  // Run this after searching so that the filtered list.
  applyDateFilter(filteredList: SprogLagLanguageMap[]): SprogLagLanguageMap[] {
    const date = this.dateControl.value ? new Date(this.dateControl.value) : null;
    if (!date) return filteredList;

    const result = filteredList.filter(map => {
      if (!map.updatedOn) {
        return false; // Exclude items with undefined updatedOn, they are either unsaved or erroneous.
      }
      var updatedOn = new Date(map.updatedOn);

      return this.isBefore ? updatedOn < date : updatedOn > date;
    });

    return result;
  }



  //#endregion

  //#region Pagination

  updateVisibleItems(): void {
    this.calculateItemsPerPage();
    this.sprogLagLanguageMapFiltered = this.removeEditedItemsFromList(this.sprogLagLanguageMapFiltered);
    //edited items
    const editedItems = this.sprogLagLanguageMap.filter(map => map.isEdited);

    this.startIndex = (this.currentPage - 1) * this.itemsPerPage;
    this.endIndex = Math.min(this.startIndex + this.itemsPerPage, this.sprogLagLanguageMapFiltered.length);
    const pageItems = this.sprogLagLanguageMapFiltered.slice(this.startIndex, this.endIndex);

    this.visibleItems = [...editedItems, ...pageItems];
  }

  goToPage(page: number): void {
    const step = page - this.currentPage;
    //if unaligned and were taking a single page step, we calculate what the next page should be in order to not skip any elements.
    if (this.unaligned && Math.abs(step) === 1) {
      if (step === -1) {
        // Going one page back
        const nextIndex = this.startIndex - 1;
        this.currentPage = Math.max(1, Math.ceil(nextIndex / this.itemsPerPage));
      } else if (step === 1) {
        // Going one page forward
        const nextIndex = this.endIndex + 1;
        this.currentPage = Math.min(this.totalPages, Math.ceil(nextIndex / this.itemsPerPage));
      }
    } else {
      this.currentPage = page;
    }

    this.updateVisibleItems();
  }

  calculateItemsPerPage(): void {
    if (this.fieldNamesColumn) {
      // Læs dommen for at finde vores aktuelle højde
      const availableHeight = this.fieldNamesColumn.nativeElement.clientHeight;
      // Find ud af mængden af ikke gemte felter.
      const editedItemsCount = this.sprogLagLanguageMap.filter(map => map.isEdited).length;
      // Beregn antallet af elementer, der kan vises på en side, tag højde for redigerede elementer da de ikke skal tælles med, da de vises på alle sider.
      this.itemsPerPage = Math.floor(availableHeight / this.itemHeight) - editedItemsCount;
      // Minimum værdi for itemsPerPage er 1
      if (this.itemsPerPage < 1) {
        this.itemsPerPage = 1;
      }
      // Beregn antallet af sider
      this.totalPages = Math.ceil(this.sprogLagLanguageMapFiltered.length / this.itemsPerPage);
      // Sørg for, at totalPages altid er mindst 1
      if (this.totalPages < 1) {
        this.totalPages = 1;
      }
      // Hvis vi er på en side, der ikke længere eksisterer, skal vi gå til den sidste side.
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    }
  }

  recalculateCurrentPage() {
    this.calculateItemsPerPage();
    const middleIndex = (this.startIndex + this.endIndex) / 2;
    this.currentPage = Math.min(this.totalPages, Math.floor(middleIndex / this.itemsPerPage) + 1);

    this.unaligned = true;
  }

  //#endregion

  //#region Flow
  handleKeybinds(event: KeyboardEvent): void {
    /* cannot rebind Ctrl-N, Ctrl-Shift-N, Ctrl-T, Ctrl-Shift-T, Ctrl-W */
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      this.saveAll();
    }
    if (event.altKey && event.key === 'n') {
      event.preventDefault();
      this.addNewTranslation();
    }
    // Udkommenteret mens jeg færdigør i anden branch.
    // if (event.ctrlKey && event.key === 'z') {
    //   event.preventDefault();
    //   this.undo();
    // }
  }

  keydownFieldname(event: KeyboardEvent, map: SprogLagLanguageMap): void {
    if (event.ctrlKey && event.shiftKey && event.key.toUpperCase() === 'C') {
      event.preventDefault();
      this.copyToClipboard(map.feltNavn);
    }
  }

  copyToClipboard(text: string): void {
    const formattedString = `{{ '${text}' | terms }}`;
    navigator.clipboard.writeText(formattedString);
  }

  formatFieldNameInput(event: Event, map: SprogLagLanguageMap): void {
    const inputElement = event.target as HTMLInputElement;
    let newValue = inputElement.value;
    // Modify the input value as needed and then let it continue
    newValue = newValue.toUpperCase()
      .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
      .replace(/_ +/g, '_') // Replace multiple underscores with a single underscore
      .replace(/ /g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with a single underscore
      .replace(/æ/g, 'ae').replace(/Æ/g, 'AE') // æåø
      .replace(/ø/g, 'oe').replace(/Ø/g, 'OE')
      .replace(/å/g, 'aa').replace(/Å/g, 'AA')
      .replace(/\W+/g, ''); // Remove all special characters except for letters, numbers and underscores

    // Update the input element's value
    // inputElement.value = newValue;
    map.feltNavn = newValue;
    this.selectedFieldNameChanged(map, newValue);
  }

  selectedFieldNameChanged(map: SprogLagLanguageMap, value: string): void {
    console.log(this.searchSelectedFieldname);

    if (map.updatedOn) return; // This only applies to fields that are being created.
    if (!this.searchSelectedFieldname) return // Only apply if the option is selected
    this.searchControlFieldname.setValue(value);
    console.log(value);

  }

  handleCsvPaste(event: ClipboardEvent, map: SprogLagLanguageMap): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedHtml = clipboardData.getData('text/html');
    const parser = new DOMParser();
    const doc = parser.parseFromString(pastedHtml, 'text/html');
    const table = doc.querySelector('table');

    if (!table) {
      return; //Bailout fordi det ikke er en tabel paste.
    }
    event.preventDefault();

    const rows = Array.from(table.querySelectorAll('tr'));
    const cultureIds = this.cultureIds;

    // Primær række, redigerer det valgte felt.
    const primaryRow = rows[0];
    const primaryCells = Array.from(primaryRow.querySelectorAll('td'));
    // Gem de nuværende værdier for at kunne lave en undo.
    const primaryChanges: ChangesToUndo = {
      ref: map,
      wasEdited: map.isEdited ?? false,
      action: 'edit',
      feltnavn: map.feltNavn,
      sproglagList: Object.entries(map.languageMap).map(([key, value]) => ({ vaerdi: value.vaerdi, cultureId: key, wasEdited: value.isEdited ?? false }))
    };
    map.feltNavn = primaryCells[0].textContent?.trim() || '';

    // Udfyld sprogværdier.
    cultureIds.forEach((cultureId, index) => {
      map.languageMap[cultureId] = map.languageMap[cultureId] || this.createBlankSprogLag('', cultureId);
      map.languageMap[cultureId].vaerdi = primaryCells[index + 1]?.textContent?.trim() || '';
    });
    map.isEdited = true;

    const secondaryChanges: ChangesToUndo[] = [];

    // Sekundære rækker, opretter nye.
    rows.slice(1).forEach(row => {
      const cells = Array.from(row.querySelectorAll('td'));
      const newMap: SprogLagLanguageMap = {
        feltNavn: cells[0].textContent?.trim() || '',
        languageMap: cultureIds.reduce((acc, cultureId, index) => {
          acc[cultureId] = this.createBlankSprogLag('', cultureId);
          acc[cultureId].vaerdi = cells[index + 1]?.textContent?.trim() || '';
          return acc;
        }, {} as { [key: string]: SprogLag }),
        isEdited: true, // Mark the new item as edited
        waitingForNetwork: false,
      };

      secondaryChanges.push({
        ref: newMap,
        wasEdited: false,
        action: 'add',
        feltnavn: '',
        sproglagList: []
      });

      this.sprogLagLanguageMap.unshift(newMap); // Add the new item to the top of the list
      this.visibleItems.unshift(newMap);
      this.visibleItems.pop(); // Remove the last item from the list
    });
    this.pushToUndoStack("Pasted CSV", [primaryChanges, ...secondaryChanges]);

    this.updateVisibleItems();
    const index = this.visibleItems.indexOf(map);
    this.focusIndex(index);
  }

  pushToUndoStack(message: string, delta: ChangesToUndo[]): void {
    const undoAction: UndoAction = {
      message: message,
      changelist: delta
    };
    this.undoStack.push(undoAction);
  }

  undo(): void {
    const undoAction = this.undoStack.pop();
    if (!undoAction) return;

    for (const change of undoAction.changelist) {
      if (change.action === 'add') {
        const index = this.sprogLagLanguageMap.indexOf(change.ref);
        if (index !== -1) {
          this.sprogLagLanguageMap.splice(index, 1);
        }
      } else if (change.action === 'edit') {
        const map = change.ref
        if (!map) continue;

        map.feltNavn = change.feltnavn;
        map.isEdited = change.wasEdited;
        for (const sproglag of change.sproglagList) {
          const cultureId = sproglag.cultureId;
          if (map.languageMap[cultureId]) {
            map.languageMap[cultureId].vaerdi = sproglag.vaerdi;
            map.languageMap[cultureId].isEdited = sproglag.wasEdited;
          }
        }
      }
    }

    this.search();
    this.updateVisibleItems();
  }

  removeFromList(map: SprogLagLanguageMap): void {
    const index = this.sprogLagLanguageMap.indexOf(map);
    if (index !== -1) {
      this.sprogLagLanguageMap.splice(index, 1);
    }
  }

  //#endregion

  //#region Focus control

  // Focus the first input field
  focusFirst() {
    setTimeout(() => {
      if (!this.constantInput) return;
      const input = this.constantInput.nativeElement as HTMLInputElement;
      if (!input) return;

      input.focus();
    }, 0);
  }

  focusIndex(index: number) {
    setTimeout(() => {
      const input = document.getElementById(`fieldName${index}`) as HTMLInputElement;
      if (!input) return;
      input.focus();
    }, 0);
  }


  //#endregion

  //#region Dom manipulation

  onResize() {
    this.updateVisibleItems();
  }

  //#endregion

  //#region Ai Translate

  async translateEmptyFields(map: SprogLagLanguageMap, sproglag: SprogLag): Promise<void> {
    const sourceLanguage = sproglag.cultureId.substring(0, 2); // Extract the first 2 characters
    const isBlank = sproglag.vaerdi.trim() === '';
    if (isBlank) return;

    const emptyFields = Object.entries(map.languageMap)
      .filter(([_, value]) => value.vaerdi.trim() === '')
      .map(([_, value]) => value);

    if (emptyFields.length === 0) return;

    const tasks = emptyFields.map(async (field) => {
      const targetLanguage = field.cultureId.substring(0, 2); // Extract the first 2 characters
      try {
        const translatedText = await this.translationsService.translateText(sproglag.vaerdi, sourceLanguage, targetLanguage);
        field.vaerdi = translatedText;
        map.isEdited = true;
        field.isEdited = true;
      } catch (error: any) {
        const message = error?.message ?? String(error);
        this.errorService.notify(message, { title: 'Fejl ved oversættelse' });
      }
    });

    await Promise.all(tasks);
  }

  //#endregion
}
